/**
 * 数据总览热力图默认视野：按网格密度框「主聚集区」，
 * 避免全量 fitBounds / 固定过小 zoom 导致热力色块看不见。
 * 谷歌（HeatMap）与天地图（rlMapCompents）共用。
 */

const DEFAULT_HEATMAP_CENTER = { lat: 39.9042, lng: 116.4074 }
const GRID_SIZE = 24
const NEIGHBOR_RATIO = 0.3
const MIN_ZOOM = 6
const MAX_ZOOM = 14
const SINGLE_POINT_ZOOM = 12

function isValidPoint (p) {
  if (!p) return false
  const { lat, lng } = p
  if (lat == null || lat === '' || lng == null || lng === '') return false
  const latNum = Number(lat)
  const lngNum = Number(lng)
  return Number.isFinite(latNum) && Number.isFinite(lngNum)
}

function clampZoom (z) {
  return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z))
}

/**
 * 由经纬度跨度估算 zoom（大致对应 Web 墨卡托级别）。
 */
function zoomFromSpan (latSpan, lngSpan) {
  const span = Math.max(latSpan, lngSpan, 1e-6)
  // log2(360/span) 近似；再减一点 padding
  const z = Math.floor(Math.log2(360 / span)) - 1
  return clampZoom(z)
}

function boundsOf (pts) {
  let minLat = Infinity
  let maxLat = -Infinity
  let minLng = Infinity
  let maxLng = -Infinity
  pts.forEach((p) => {
    const lat = Number(p.lat)
    const lng = Number(p.lng)
    if (lat < minLat) minLat = lat
    if (lat > maxLat) maxLat = lat
    if (lng < minLng) minLng = lng
    if (lng > maxLng) maxLng = lng
  })
  return {
    sw: { lat: minLat, lng: minLng },
    ne: { lat: maxLat, lng: maxLng },
    center: {
      lat: (minLat + maxLat) / 2,
      lng: (minLng + maxLng) / 2
    }
  }
}

/** 去掉最外约 5% 分位后的点（兜底） */
function trimOutliers (pts) {
  if (pts.length < 10) return pts
  const lats = pts.map((p) => Number(p.lat)).sort((a, b) => a - b)
  const lngs = pts.map((p) => Number(p.lng)).sort((a, b) => a - b)
  const lo = Math.floor(pts.length * 0.05)
  const hi = Math.max(lo + 1, Math.ceil(pts.length * 0.95) - 1)
  const latLo = lats[lo]
  const latHi = lats[hi]
  const lngLo = lngs[lo]
  const lngHi = lngs[hi]
  return pts.filter((p) => {
    const lat = Number(p.lat)
    const lng = Number(p.lng)
    return lat >= latLo && lat <= latHi && lng >= lngLo && lng <= lngHi
  })
}

/**
 * @param {Array<{lat:number,lng:number}>} points
 * @returns {{ center:{lat:number,lng:number}, sw:{lat:number,lng:number}, ne:{lat:number,lng:number}, suggestedZoom:number }}
 */
function computeDenseClusterView (points) {
  const valid = (points || []).filter(isValidPoint)
  if (!valid.length) {
    return {
      center: { ...DEFAULT_HEATMAP_CENTER },
      sw: { ...DEFAULT_HEATMAP_CENTER },
      ne: { ...DEFAULT_HEATMAP_CENTER },
      suggestedZoom: clampZoom(8)
    }
  }
  if (valid.length === 1) {
    const p = { lat: Number(valid[0].lat), lng: Number(valid[0].lng) }
    return {
      center: p,
      sw: p,
      ne: p,
      suggestedZoom: Math.max(SINGLE_POINT_ZOOM, MIN_ZOOM)
    }
  }

  const all = boundsOf(valid)
  let latSpan = all.ne.lat - all.sw.lat
  let lngSpan = all.ne.lng - all.sw.lng
  // 跨度过小：设下限，避免一格装下全部点
  const minSpan = 0.02
  if (latSpan < minSpan) latSpan = minSpan
  if (lngSpan < minSpan) lngSpan = minSpan

  const cellH = latSpan / GRID_SIZE
  const cellW = lngSpan / GRID_SIZE
  const originLat = all.sw.lat
  const originLng = all.sw.lng

  const counts = new Array(GRID_SIZE * GRID_SIZE).fill(0)
  const buckets = new Array(GRID_SIZE * GRID_SIZE)
  for (let i = 0; i < buckets.length; i++) buckets[i] = []

  valid.forEach((p) => {
    const lat = Number(p.lat)
    const lng = Number(p.lng)
    let row = Math.floor((lat - originLat) / cellH)
    let col = Math.floor((lng - originLng) / cellW)
    row = Math.min(GRID_SIZE - 1, Math.max(0, row))
    col = Math.min(GRID_SIZE - 1, Math.max(0, col))
    const idx = row * GRID_SIZE + col
    counts[idx]++
    buckets[idx].push(p)
  })

  let peak = 0
  let peakIdx = 0
  for (let i = 0; i < counts.length; i++) {
    if (counts[i] > peak) {
      peak = counts[i]
      peakIdx = i
    }
  }

  // 峰值格 + 8 邻域中点数 ≥ 峰值 30% 的格子 → 主聚集窗口
  const peakRow = Math.floor(peakIdx / GRID_SIZE)
  const peakCol = peakIdx % GRID_SIZE
  const threshold = peak * NEIGHBOR_RATIO
  const windowPts = []
  for (let dr = -1; dr <= 1; dr++) {
    for (let dc = -1; dc <= 1; dc++) {
      const r = peakRow + dr
      const c = peakCol + dc
      if (r < 0 || r >= GRID_SIZE || c < 0 || c >= GRID_SIZE) continue
      const idx = r * GRID_SIZE + c
      if (counts[idx] >= threshold) {
        windowPts.push.apply(windowPts, buckets[idx])
      }
    }
  }

  let usePts = windowPts
  if (usePts.length < Math.max(3, Math.ceil(valid.length * 0.05))) {
    // 窗口过稀：分位裁剪兜底
    usePts = trimOutliers(valid)
  }

  const b = boundsOf(usePts)
  const suggestedZoom = zoomFromSpan(b.ne.lat - b.sw.lat, b.ne.lng - b.sw.lng)
  return {
    center: b.center,
    sw: b.sw,
    ne: b.ne,
    suggestedZoom
  }
}

module.exports = {
  DEFAULT_HEATMAP_CENTER,
  computeDenseClusterView
}
